import { Box, Typography, IconButton, Stack } from "@mui/material";
import React, { useRef } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import parse from "html-react-parser";

function PreparationStepsDialog({ state, data, onImageClick }) {
  const scrollContainerRef = useRef(null);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -150,
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 150,
        behavior: "smooth",
      });
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          maxHeight: "60vh",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#DFE8F2",
            borderRadius: "4px",
          },
          gap: "16px",
          pt: 1,
          pb: 2,
          px: 2,
          flex: 1,
        }}
      >
        {/* Image Section */}
        {state?.image_url ? (
          <Box
            component="img"
            src={state?.image_url}
            sx={{
              width: "100%",
              height: "300px",
              objectFit: "contain",
              flexShrink: 0,
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "250px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#F2F7FC",
              borderRadius: "8px",
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={"/icons/ic_image.svg"}
              sx={{ width: "24px", height: "24px", objectFit: "contain" }}
            />
          </Box>
        )}

        {/* Description Section - Scrollable */}
        <Box
          sx={{
            flex: 1,
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#DFE8F2",
              borderRadius: "4px",
            },
          }}
        >
          <Typography fontSize="16px" fontWeight={600}>
            Step {state?.step_number}:
          </Typography>
          <Typography
            variant="body1"
            fontSize="14px"
            fontWeight={400}
            lineHeight="16.94px"
          >
            {state?.step
              ? parse(state.step)
              : "Step description not available."}
          </Typography>
        </Box>
      </Box>

      {/* Thumbnails Section - Fixed at bottom */}
      <Box
        sx={{
          position: "relative",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <IconButton
          onClick={scrollLeft}
          size="small"
          sx={{
            position: "absolute",
            left: 0,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 10,
            backgroundColor: "#fff",
            "&:hover": {
              backgroundColor: "#fff",
              opacity: 0.9,
            },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>

        <Stack
          ref={scrollContainerRef}
          className="scroll-hide"
          direction="row"
          sx={{
            display: "flex",
            gap: "8px",
            overflowX: "auto",
            padding: "20px",
            alignItems: "center",
            height: "122px",
            ml: "16px",
          }}
        >
          {data.map((item) => {
            const isSelected = item.step_number === state.step_number;
            return (
              <Box
                key={item.id}
                onClick={() => onImageClick(item)}
                sx={{
                  width: isSelected ? "116px" : "96px",
                  height: isSelected ? "82px" : "67px",
                  backgroundColor: "#F2F7FC",
                  cursor: "pointer",
                  border: isSelected ? "3px solid" : "0",
                  borderColor: isSelected ? "primary.light" : "",
                  borderRadius: "4px",
                  overflow: "hidden",
                  position: "relative",
                  flexShrink: 0,
                  transition: "all 0.3s ease-in-out",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {item?.image_url ? (
                  <Box
                    component="img"
                    src={item.image_url}
                    alt={`Step ${item.step_number}`}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <Box
                    component="img"
                    src={"/icons/ic_image.svg"}
                    alt={`Step ${item.step_number}`}
                    sx={{
                      height: "24px",
                      width: "24px",
                      opacity: 0.5,
                    }}
                  />
                )}

                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background:
                      "linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.7))",
                    color: "#fff",
                    padding: "8px 4px 4px",
                    fontSize: "12px",
                    height: "35px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  Step {item.step_number}
                </Box>
              </Box>
            );
          })}
        </Stack>

        <IconButton
          onClick={scrollRight}
          sx={{
            position: "absolute",
            right: 0,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 10,
            backgroundColor: "#fff",
            "&:hover": {
              backgroundColor: "#fff",
              opacity: 0.9,
            },
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
}

export default PreparationStepsDialog;
